// Aggregates the live counts shown on the admin Dashboard tiles, entirely from
// existing list endpoints (single-cluster scale is small, so counting returned
// rows is cheap enough — no dedicated stats endpoint).
//
// Each source is independently fault-tolerant: a 403 (staff lacks a permission)
// or a network error for one source yields `null` for that tile (rendered as
// "—") rather than failing the whole dashboard.

import { getAdminNotifications } from './notifications-api';
import { listAdminUnits } from './units-api';
import { listResidents } from './residents-api';
import { listInvoices } from './invoices-api';

export interface DashboardStats {
  units: number | null;
  residents: number | null;
  openInvoices: number | null;
  pendingBookings: number | null;
  openComplaints: number | null;
  activeEmergencies: number | null;
}

// Cap on the OPEN-invoice fetch used purely for counting. Single-cluster lists
// are far smaller; this just bounds the paginated read.
const COUNT_LIMIT = 100;

export async function getDashboardStats(): Promise<DashboardStats> {
  // Ops counts (bookings / complaints / emergencies) reuse the already-polled
  // notification-bell aggregator. `since` only affects packages/scans, which
  // the dashboard doesn't surface, so "now" is fine.
  const notifications = getAdminNotifications(new Date().toISOString()).catch(() => null);

  const [units, residents, openInvoices, notif] = await Promise.all([
    listAdminUnits()
      .then((rows) => rows.length)
      .catch(() => null),
    listResidents()
      .then((rows) => rows.length)
      .catch(() => null),
    listInvoices({ status: 'OPEN', limit: COUNT_LIMIT })
      .then((r) => r.items.length)
      .catch(() => null),
    notifications,
  ]);

  return {
    units,
    residents,
    openInvoices,
    pendingBookings: notif ? notif.bookingsPending : null,
    openComplaints: notif ? notif.complaintsNew : null,
    activeEmergencies: notif ? notif.emergenciesActive : null,
  };
}
